import { useState, type ReactNode } from 'react';
import { RotateCcw, Search, SlidersHorizontal, X } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { Sheet } from '@/components/ui/layout';
import { Button, Input, Select, statusLabel } from '@/components/ui/primitives';
import { BOARD_STATUSES, activeFilterCount, type BoardFilter, type StatusCounts, type StatusFilter } from './bed-status';
import { STATUS_BAR, bedClassLabel } from './bed-board-look';
import { StatusMiniBar } from './bed-board-art';

const SEXES = ['male', 'female', 'other'] as const;

export interface WardChoice {
  wardId: string;
  wardName: string;
  counts: StatusCounts;
}

function WardChip({ name, counts, active, onClick }: { name: string; counts: StatusCounts; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={tr('ipd', 'ward_occupancy_x', { ward: name, occupied: counts.occupied, total: counts.total })}
      onClick={onClick}
      className={cn(
        'flex w-40 shrink-0 flex-col gap-2 rounded-xl border bg-card px-3 py-2.5 text-start shadow-sm transition-[border-color,background-color,box-shadow,transform] duration-200 focus-ring motion-safe:active:scale-[0.98]',
        active ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/30' : 'hover:border-primary/30 hover:shadow-md',
      )}
    >
      <span className="flex items-center justify-between gap-2">
        <span className={cn('truncate text-sm font-semibold', active && 'text-primary')}>{name}</span>
        <span className="shrink-0 text-xs font-medium text-muted-foreground tabular">
          {counts.occupied}/{counts.total}
        </span>
      </span>
      <StatusMiniBar counts={counts} />
    </button>
  );
}

export function WardChips({
  wards,
  allCounts,
  wardId,
  onChange,
}: {
  wards: WardChoice[];
  allCounts: StatusCounts;
  wardId: string;
  onChange: (wardId: string) => void;
}) {
  return (
    <div role="group" aria-label={tr('ipd', 'wards')} className="-mx-1 mb-4 flex gap-2 overflow-x-auto px-1 pb-1.5 pt-0.5">
      <WardChip name={tr('ipd', 'all_wards')} counts={allCounts} active={wardId === ''} onClick={() => onChange('')} />
      {wards.map((ward) => (
        <WardChip key={ward.wardId} name={ward.wardName} counts={ward.counts} active={ward.wardId === wardId} onClick={() => onChange(ward.wardId)} />
      ))}
    </div>
  );
}

export function StatusLegend({ counts, className }: { counts: StatusCounts; className?: string }) {
  return (
    <ul aria-label={tr('ipd', 'legend')} className={cn('flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground', className)}>
      {BOARD_STATUSES.map((status) => (
        <li key={status} className="flex items-center gap-1.5">
          <span className={cn('h-2.5 w-2.5 rounded-full', STATUS_BAR[status])} aria-hidden />
          <span>{statusLabel(status)}</span>
          <span className="font-medium text-foreground tabular">{counts[status]}</span>
        </li>
      ))}
    </ul>
  );
}

function SearchBox({ value, onChange, className }: { value: string; onChange: (next: string) => void; className?: string }) {
  return (
    <div className={cn('relative min-w-0', className)}>
      <Search className="pointer-events-none absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <Input
        value={value}
        aria-label={tr('ipd', 'search_the_board')}
        placeholder={tr('ipd', 'name_mrn_or_bed')}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && value) {
            event.preventDefault();
            onChange('');
          }
        }}
        className="pe-8 ps-8"
      />
      {value && (
        <button
          type="button"
          aria-label={tr('ipd', 'clear_filters')}
          onClick={() => onChange('')}
          className="absolute end-1.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-ring"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}

function LabelledControl({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn('flex min-w-0 flex-col gap-1', className)}>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function FilterControls({
  filter,
  bedClasses,
  onChange,
  stacked,
}: {
  filter: BoardFilter;
  bedClasses: string[];
  onChange: (next: BoardFilter) => void;
  stacked?: boolean;
}) {
  const width = stacked ? 'w-full' : 'w-44';
  return (
    <>
      <LabelledControl label={tr('ipd', 'status')} className={width}>
        <Select value={filter.status} onChange={(event) => onChange({ ...filter, status: event.target.value as StatusFilter })}>
          <option value="all">{tr('ipd', 'all_statuses')}</option>
          {BOARD_STATUSES.map((status) => (
            <option key={status} value={status}>
              {statusLabel(status)}
            </option>
          ))}
          <option value="discharge_today">{tr('ipd', 'discharging_today')}</option>
        </Select>
      </LabelledControl>
      <LabelledControl label={tr('ipd', 'bed_class')} className={width}>
        <Select value={filter.bedClass} onChange={(event) => onChange({ ...filter, bedClass: event.target.value })}>
          <option value="">{tr('ipd', 'any_class')}</option>
          {bedClasses.map((bedClass) => (
            <option key={bedClass} value={bedClass}>
              {bedClassLabel(bedClass)}
            </option>
          ))}
        </Select>
      </LabelledControl>
      <LabelledControl label={tr('ipd', 'patient_sex')} className={stacked ? 'w-full' : 'w-36'}>
        <Select value={filter.sex} onChange={(event) => onChange({ ...filter, sex: event.target.value })}>
          <option value="">{tr('ipd', 'any_sex')}</option>
          {SEXES.map((sex) => (
            <option key={sex} value={sex}>
              {statusLabel(sex)}
            </option>
          ))}
        </Select>
      </LabelledControl>
    </>
  );
}

export function BoardFilterBar({
  filter,
  bedClasses,
  counts,
  matching,
  onChange,
  onReset,
}: {
  filter: BoardFilter;
  bedClasses: string[];
  counts: StatusCounts;
  matching: number;
  onChange: (next: BoardFilter) => void;
  onReset: () => void;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const active = activeFilterCount(filter);
  const sheetFilters = activeFilterCount({ ...filter, search: '' });

  return (
    <div className="mb-5 rounded-xl border border-border/70 bg-card px-3 py-3 shadow-sm sm:px-4">
      <div className="flex items-end gap-2 md:hidden">
        <SearchBox value={filter.search} onChange={(search) => onChange({ ...filter, search })} className="flex-1" />
        <Button variant="outline" onClick={() => setSheetOpen(true)} className="relative shrink-0">
          <SlidersHorizontal className="h-4 w-4" aria-hidden />
          {tr('ipd', 'filters')}
          {sheetFilters > 0 && (
            <span className="grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground tabular">
              {sheetFilters}
            </span>
          )}
        </Button>
      </div>

      <div className="hidden flex-wrap items-end gap-3 md:flex">
        <LabelledControl label={tr('ipd', 'search_the_board')} className="min-w-[14rem] flex-1 lg:max-w-sm">
          <SearchBox value={filter.search} onChange={(search) => onChange({ ...filter, search })} />
        </LabelledControl>
        <FilterControls filter={filter} bedClasses={bedClasses} onChange={onChange} />
        {active > 0 && (
          <Button variant="ghost" onClick={onReset} className="text-muted-foreground">
            <RotateCcw className="h-4 w-4" aria-hidden />
            {tr('ipd', 'clear_filters')}
          </Button>
        )}
      </div>

      <StatusLegend counts={counts} className="mt-3 border-t border-border/60 pt-3" />

      {sheetOpen && (
        <Sheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={tr('ipd', 'filters')}
          footer={
            <>
              {active > 0 && (
                <Button variant="ghost" onClick={onReset}>
                  <RotateCcw className="h-4 w-4" aria-hidden />
                  {tr('ipd', 'clear_filters')}
                </Button>
              )}
              <Button onClick={() => setSheetOpen(false)}>{tr('ipd', 'show_x_beds', { count: matching })}</Button>
            </>
          }
        >
          <div className="space-y-4">
            <FilterControls filter={filter} bedClasses={bedClasses} onChange={onChange} stacked />
            <StatusLegend counts={counts} className="border-t border-border/60 pt-4" />
          </div>
        </Sheet>
      )}
    </div>
  );
}
